import { ArrowPathIcon, EnvelopeIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@/components/Dialog'
import { TextField } from '@/components/TextField'

type EmailVerificationDialogProps = {
    mode: 'setup' | 'manage'
    onClose: () => void
    onVerify: (code: string) => Promise<void>
    onResend: () => Promise<void>
    onDisable: () => Promise<void>
}

export const EmailVerificationDialog = ({
    mode,
    onClose,
    onVerify,
    onResend,
    onDisable,
}: EmailVerificationDialogProps) => {
    const { t } = useTranslation()

    const codeInputRef = useRef<HTMLInputElement | null>(null)
    const cancelRef = useRef<HTMLButtonElement | null>(null)

    const [code, setCode] = useState('')
    const [confirming, setConfirming] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)

    let title = t('security.local.email.title')
    if (mode === 'setup') {
        title = t('security.local.email.dialog.setup.title')
    } else if (confirming) {
        title = t('security.local.email.dialog.disable.title')
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        setError(null)

        try {
            if (mode === 'setup') {
                await onVerify(code)
            } else {
                await onDisable()
            }
        } catch (cause) {
            let fallback = t('security.local.email.messages.disableError')

            if (mode === 'setup') {
                fallback = t('security.local.email.messages.verifyError')
            }

            setError(cause instanceof Error ? cause.message : fallback)
        } finally {
            setSubmitting(false)
        }
    }

    const handleResend = async () => {
        setSubmitting(true)

        try {
            await onResend()
        } catch {
            // The mutation displays resend errors in the account status banner.
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog
            open
            title={title}
            onClose={onClose}
            closeLabel={t('common.close')}
            dismissible={!submitting}
            initialFocusRef={mode === 'setup' ? codeInputRef : cancelRef}
            footer={
                <div className="flex flex-wrap justify-end gap-3">
                    <button
                        ref={cancelRef}
                        type="button"
                        disabled={submitting}
                        onClick={() => {
                            if (confirming) {
                                setConfirming(false)
                                setError(null)
                            } else {
                                onClose()
                            }
                        }}
                        className="border-account-border bg-account-surface text-account-primary hover:bg-account-muted rounded-lg border px-4 py-2 font-semibold disabled:opacity-60"
                    >
                        {mode === 'setup' || confirming ? t('common.cancel') : t('common.close')}
                    </button>
                    {mode === 'setup' ? (
                        <button
                            type="button"
                            onClick={() => {
                                void handleSubmit()
                            }}
                            disabled={submitting || !code}
                            className="bg-account-accent text-account-on-accent hover:bg-account-accent-hover rounded-lg px-6 py-2 font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {t('common.verifyCode')}
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={submitting}
                            onClick={() => {
                                if (confirming) {
                                    void handleSubmit()
                                } else {
                                    setConfirming(true)
                                    cancelRef.current?.focus()
                                }
                            }}
                            className="border-account-border text-account-error hover:bg-account-error-soft inline-flex items-center justify-center gap-2 rounded-lg border px-4 py-2 font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting && <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />}
                            {t('security.local.email.dialog.disable.action')}
                        </button>
                    )}
                </div>
            }
        >
            {mode === 'setup' ? (
                <>
                    <div className="border-account-divider bg-account-muted text-account-primary flex items-start gap-3 rounded-lg border px-4 py-3 text-sm">
                        <EnvelopeIcon className="text-account-secondary mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                        <p>{t('security.local.email.dialog.setup.description')}</p>
                    </div>
                    <div className="space-y-2">
                        <TextField
                            inputRef={codeInputRef}
                            label={t('security.local.email.dialog.setup.codeLabel')}
                            value={code}
                            placeholder="000000"
                            centered
                            onChange={(value) => {
                                setCode(value)
                                setError(null)
                            }}
                            error={error}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                void handleResend()
                            }}
                            disabled={submitting}
                            className="text-account-secondary hover:text-account-strong ml-auto block text-sm font-semibold underline underline-offset-4 transition disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {t('security.local.email.dialog.setup.resend')}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    {!confirming && (
                        <p className="text-account-success text-sm font-medium">{t('security.local.status.enabled')}</p>
                    )}
                    <p className="text-account-secondary text-sm">
                        {confirming
                            ? t('security.local.email.dialog.disable.description')
                            : t('security.local.email.dialog.manage.description')}
                    </p>
                    {error && (
                        <p role="alert" className="text-account-error text-sm">
                            {error}
                        </p>
                    )}
                </>
            )}
        </Dialog>
    )
}
